<template>
  <EditorPickLotteryPickerSection data-containerid="pick_lottery_editor_numbers">
    <LcFormField
      class="flex flex-col items-center"
      v-slot="{ field }"
      mode="passive"
      name="numbers"
    >
      <LcDigitInputGroup
        ref="inputGroup"
        :class="['flex flex-col items-center', { 'pick-lottery-digits-locked': locked }]"
        v-bind="field"
        v-slot="{ setRef }"
        :value="numbers"
      >
        <div
          v-for="({ name }, index) in config.numbers"
          :key="name"
          :class="['pick-lottery-digit', digitClass(name, index)]"
        >
          <LcDigitInput
            v-bind="{ index }"
            :ref="setRef"
            aria-labelledby="number-picker-label"
            @click="inputGroupRef?.focusInputByIndex(index)"
            :disabled="isDisabled(name)"
            @update:value="(value) => handleSetNumber(name, value)"
          />
        </div>
      </LcDigitInputGroup>
    </LcFormField>

    <LcInfoText v-if="isLockoutLottery" class="text-secondary-secondary" size="small">
      {{ $t('picklottery.disclaimer.lockoutlottery') }}
    </LcInfoText>
  </EditorPickLotteryPickerSection>
</template>

<script lang="ts" setup>
import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config } from '../@types/editor'

import { EDITOR_QUICK_PICK_MOTION } from '../constants/quickPickAnimation'
import { type EditorPlay, usePickLotteryStore } from '../stores/pickLottery'
import { findFieldIndexByName, getSelectedNumbers } from '../utils/editorFields'

type Props = {
  config: Config
  lotteryId: LotteryId
  play: EditorPlay
}

const { config, lotteryId, play } = defineProps<Props>()

const store = usePickLotteryStore(lotteryId)
const { isDisabled } = usePickLotteryDisabledFields(
  lotteryId,
  () => config,
  () => play,
)
const { getNumberState, getSummaryPlay, isAnimating } = useEditorQuickPickAnimation()

const inputGroupRef = useTemplateRef('inputGroup')

const isLockoutLottery = computed(() => !!config.isLockoutLottery)

const locked = computed(() => isAnimating(play.id))

const numbers = computed(() => getSelectedNumbers(getSummaryPlay(play).fields))

const digitClass = (name: string, index: number) => {
  const state = getNumberState(play.id, numbers.value[index] ?? undefined, name)
  return state ? `pick-lottery-digit-${state}` : ''
}

const handleSetNumber = (name: string, value: Maybe<number>) => {
  if (locked.value) return

  const index = findFieldIndexByName(play, name)
  const newValue = typeof value === 'number' ? [value] : []
  store.setSelectedNumbers(play.id, index, newValue)
}

const quickPickMotion = EDITOR_QUICK_PICK_MOTION
</script>

<style lang="scss" scoped>
$roll-tick: v-bind("quickPickMotion.tick + 'ms'");
$settle-duration: v-bind("quickPickMotion.settle + 'ms'");

.pick-lottery-digits-locked {
  pointer-events: none;
}

.pick-lottery-digit-rolling {
  /* stylelint-disable-next-line selector-class-pattern */
  :deep(.lc-input__wrap) {
    overflow: hidden;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  :deep(.lc-input__input-wrap input) {
    animation: pick-lottery-digit-roll $roll-tick linear infinite;
  }
}

.pick-lottery-digit-locked {
  /* stylelint-disable-next-line selector-class-pattern */
  :deep(.lc-input__wrap) {
    animation: pick-lottery-digit-box-settle $settle-duration $transition-ease-pop;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  :deep(.lc-input__input-wrap input) {
    animation: pick-lottery-digit-settle $settle-duration $transition-ease-pop;
  }
}

@keyframes pick-lottery-digit-roll {
  from {
    opacity: 0.35;
    transform: translateY(-10%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pick-lottery-digit-box-settle {
  from {
    transform: scale(1.05);
  }

  to {
    transform: scale(1);
  }
}

@keyframes pick-lottery-digit-settle {
  from {
    transform: scale(1.07);
  }

  to {
    transform: scale(1);
  }
}
</style>
